import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "@storytell/markdown";
import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent } from "@storytell/streaming";
import { TbChevronDown, TbRobot } from "solid-icons/tb";
import { createEffect, createMemo, createSignal, Show } from "solid-js";
import { defaultMarkdownOptions } from "../block/markdownOptions";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../ui";
import { collapsibleOpenHint } from "../util/collapsibleOpenHint";
import { getScopeBadgeClasses } from "./activityUIHelpers";
import { SkillCategoryIcon } from "./skillCategoryIcon";

export interface SkillsData {
  type: "skills";
  version: number;
  skillName: string;
  displayName: string;
  scope: string;
  content?: string;
  context?: "agent";
  agent?: string;
  /** When set by backend, drives header icon (skill vs persona). */
  category?: "skill" | "persona";
}

interface SkillsRendererProps {
  block: StreamingBlock;
}

export function SkillsRenderer(props: SkillsRendererProps) {
  const rt = useChatRuntime();
  const [isExpanded, setIsExpanded] = createSignal(false);

  const skillsData = createMemo<SkillsData | null>(() => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as SkillsData | null;
      if (parsed?.type === "skills") return parsed;
    }
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as SkillsData | null;
      if (parsed?.type === "skills") return parsed;
    }
    return null;
  });

  const markdownStreamer = createMemo(() => {
    const data = skillsData();
    if (!data?.content) return null;
    return createSolidStreamingMarkdown();
  });

  createEffect(() => {
    const streamer = markdownStreamer();
    const data = skillsData();
    if (streamer && data?.content) {
      streamer.reset();
      streamer.append(data.content);
    }
  });

  const resolvedCategory = createMemo((): "skill" | "persona" => {
    const data = skillsData();
    if (!data) return "skill";
    if (data.category === "persona" || data.category === "skill") return data.category;
    const list = rt.ui?.skills?.() ?? [];
    const byName = list.find((s) => s.name === data.skillName)?.category;
    if (byName === "persona" || byName === "skill") return byName;
    const byDisplay = list.find((s) => s.displayName === data.displayName)?.category;
    return byDisplay === "persona" ? "persona" : "skill";
  });

  return (
    <Show when={skillsData()}>
      {(data) => (
        <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
          <div class="flex items-center gap-3 bg-gray-50 px-3 py-2.5 dark:bg-slate-800/30">
            <div class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
              <SkillCategoryIcon category={resolvedCategory()} class="size-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="truncate text-sm font-medium text-primary-text">{data().displayName}</span>
                <span class={`shrink-0 rounded px-1.5 py-0.5 text-xs ${getScopeBadgeClasses(data().scope)}`}>
                  {data().scope}
                </span>
                <Show when={data().context === "agent"}>
                  <span class="flex shrink-0 items-center gap-1 rounded bg-cyan-100 px-1.5 py-0.5 text-xs text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400">
                    <TbRobot class="size-3" />
                    {data().agent || "auto"}
                  </span>
                </Show>
              </div>
              <div class="mt-0.5 text-xs text-secondary-text">@{data().skillName}</div>
            </div>
          </div>
          <Show when={data().content}>
            <div class="border-t border-gray-100 dark:border-slate-700/50">
              <div class="group/skills-instructions flex w-full items-center gap-2 px-3 py-2 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800/20">
                <button
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  class="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                >
                  <TbChevronDown
                    class="size-3.5 shrink-0 text-secondary-text transition-transform"
                    classList={{ "rotate-180": isExpanded() }}
                  />
                  <span class="text-[10px] font-medium tracking-wide text-secondary-text/70 uppercase">
                    {rt.t("skill_instructions")}
                  </span>
                </button>
                <Show when={!isExpanded()}>
                  <span class={collapsibleOpenHint.skillsInstructions} aria-hidden>
                    {rt.t("common.open")}
                  </span>
                </Show>
              </div>
              <Show
                when={isExpanded()}
                fallback={
                  <div class="px-3 pb-2.5">
                    <div class="line-clamp-2 max-w-full text-xs [&_h1]:mt-0 [&_h1]:text-sm [&_h2]:mt-0 [&_h2]:text-sm [&_h3]:mt-0 [&_p]:mb-0 [&_p]:leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <Show when={markdownStreamer()}>
                        {(streamer) => <SolidStreamingMarkdown doc={streamer().doc} options={defaultMarkdownOptions} />}
                      </Show>
                    </div>
                  </div>
                }
              >
                <div class="px-3 pt-1 pb-3">
                  <ScrollableFadeContainer maxHeight="200px">
                    <div class="max-w-full text-xs [&_h1]:mt-0 [&_h2]:mt-0 [&_h3]:mt-0 [&_li]:leading-relaxed [&_ol]:my-2 [&_ol]:space-y-1 [&_p]:mb-2 [&_p]:leading-relaxed [&_ul]:my-2 [&_ul]:space-y-1 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <Show when={markdownStreamer()}>
                        {(streamer) => <SolidStreamingMarkdown doc={streamer().doc} options={defaultMarkdownOptions} />}
                      </Show>
                    </div>
                  </ScrollableFadeContainer>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      )}
    </Show>
  );
}
